/**
 * Status chip (design "Status system"): 22 px pill with a 6 px dot. Text
 * uses the *Fg tokens so it passes 4.5:1 on the soft background; the base
 * colour is only used for the dot.
 */
import React from 'react';

type Tone = 'neutral' | 'info' | 'brand' | 'ok' | 'danger' | 'warn' | 'muted';

const TONES: Record<Tone, { bg: string; fg: string; dot: string }> = {
  neutral: { bg: 'var(--neutralSoft)', fg: 'var(--text2)', dot: 'var(--text3)' },
  muted: { bg: 'var(--neutralSoft)', fg: 'var(--text3)', dot: 'var(--text3)' },
  info: { bg: 'var(--infoSoft)', fg: 'var(--infoFg)', dot: 'var(--info)' },
  brand: { bg: 'var(--brandSoft)', fg: 'var(--brand)', dot: 'var(--brand)' },
  ok: { bg: 'var(--posSoft)', fg: 'var(--posFg)', dot: 'var(--pos)' },
  danger: { bg: 'var(--negSoft)', fg: 'var(--negFg)', dot: 'var(--neg)' },
  warn: { bg: 'var(--warnSoft)', fg: 'var(--warnFg)', dot: 'var(--warn)' },
};

export const STATUS: Record<string, { label: string; tone: Tone }> = {
  DRAFT: { label: 'Draft', tone: 'neutral' },
  UNPAID: { label: 'Unpaid', tone: 'info' },
  PARTLY_PAID: { label: 'Partly paid', tone: 'brand' },
  PAID: { label: 'Paid', tone: 'ok' },
  OVERDUE: { label: 'Overdue', tone: 'danger' },
  PENDING_APPROVAL: { label: 'Waiting for approval', tone: 'warn' },
  VOID: { label: 'Void', tone: 'muted' },
  Q_DRAFT: { label: 'Draft', tone: 'neutral' },
  Q_SENT: { label: 'Sent', tone: 'info' },
  Q_ACCEPTED: { label: 'Accepted', tone: 'ok' },
  Q_DECLINED: { label: 'Declined', tone: 'danger' },
  Q_EXPIRED: { label: 'Expired', tone: 'muted' },
  Q_INVOICED: { label: 'Invoiced', tone: 'brand' },
};

export default function StatusChip({ status }: { status: string }) {
  const s = STATUS[status] ?? { label: status, tone: 'neutral' as Tone };
  const t = TONES[s.tone];
  return (
    <span
      data-testid="status-chip"
      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full"
      style={{ height: 22, padding: '0 9px', background: t.bg, color: t.fg, fontSize: 12, fontWeight: 600 }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 999, background: t.dot }} />
      {s.label}
    </span>
  );
}
